import React, { createContext, useContext, useMemo, useState } from "react";
import { getResponsibleInstitutes } from "../../../services/Product/ProductService/productService";
import { getBeaSafeId, handleReferenceLogic, hasFaxOrBea } from "./communicationHelperFunctions";
import MenuItem from "@mui/material/MenuItem";
import _ from "lodash";
import { translate } from "../../../services/Translations/translatorService";
import { Person } from "../../../types/Person";
import { ProductUnion } from "../../../types/ProductUnion";

interface CommunicationMediumProviderData {
  communicationMedium: string;
  setCommunicationMedium: (communicationMedium: string) => void;
  recipientType: string;
  setRecipientType: (recipientType: string) => void;
  assignedLawyerHasBea: boolean;
  communicationMediumEditable: boolean;
  recipientMenuItems: Array<JSX.Element | undefined>;
  handleClose: () => void;
  product: ProductUnion;
  externalReference: string;
  externalReferenceIsEditable: boolean;
}

const CommunicationMediumProviderContext = createContext<CommunicationMediumProviderData | null>(null);

export default function CommunicationMediumProvider({
  children,
  product,
  handleClose,
  initialCommunicationMedium = "bea",
  initialRecipientType = "",
}: {
  children?: React.ReactNode;
  product: ProductUnion;
  handleClose: () => void;
  initialCommunicationMedium?: string;
  initialRecipientType?: string;
}) {
  const [communicationMedium, setCommunicationMedium] = useState<string>(initialCommunicationMedium);
  const [recipientType, setRecipientType] = useState<string>(initialRecipientType);
  const assignedLawyerHasBea = useMemo(() => product?.backofficeCase?.lawyer?.person?.beaData, [product]);
  const recipientMenuItems = useMemo(() => {
    const { courtPath, authorityPath, prosecutionPath } = getResponsibleInstitutes(product);
    let accumulatedRecipientMenuItems = [];
    if (hasFaxOrBea(product, courtPath)) {
      accumulatedRecipientMenuItems.push(
        <MenuItem key={"court"} value={"court"}>
          {product.responsibleCourt?.name || "Zuständiges Gericht"}
        </MenuItem>
      );
    }

    if (hasFaxOrBea(product, authorityPath)) {
      accumulatedRecipientMenuItems.push(
        <MenuItem key={"authority"} value={"authority"}>
          {product.responsibleAuthority?.name || "Zuständige Behörde"}
        </MenuItem>
      );
    }

    if (hasFaxOrBea(product, prosecutionPath)) {
      accumulatedRecipientMenuItems.push(
        <MenuItem key={"prosecution"} value={"prosecution"}>
          Zuständige Staatsanwaltschaft
        </MenuItem>
      );
    }

    if (
      product.productCategory === "contractLaw" &&
      product.opposingPartyType === "opposingPartyLegalEntity" &&
      hasFaxOrBea(product, "contractualPartnerLegalEntity.organization")
    ) {
      accumulatedRecipientMenuItems.push(
        <MenuItem key={"contractualPartnerLegalEntity"} value={"contractualPartnerLegalEntity"}>
          Gegenseite
        </MenuItem>
      );
    }

    if (product.productClassName === "rentalContract" && hasFaxOrBea(product, "propertyManager.organization")) {
      accumulatedRecipientMenuItems.push(
        <MenuItem key={"propertyManager"} value={"propertyManager"}>
          Hausverwaltung
        </MenuItem>
      );
    }

    if (product.opponentLawFirm && hasFaxOrBea(product, "opponentLawFirm.organization")) {
      accumulatedRecipientMenuItems.push(
        <MenuItem key={"opposingCounsel"} value={"opposingCounsel"}>
          Rechtsanwalt Gegenseite
        </MenuItem>
      );
    }
    if (
      (product.productCategory === "contractLaw" || product.productClassName === "employmentContract") &&
      hasFaxOrBea(product, "debtCollectionAgency.organization")
    ) {
      accumulatedRecipientMenuItems.push(
        <MenuItem key={"debtCollectionAgency"} value={"debtCollectionAgency"}>
          Inkassobüro
        </MenuItem>
      );
    }
    if (product.processParticipantsOrganizations) {
      const processParticipantsOrganizationsMenuItems = _.map(
        product.processParticipantsOrganizations,
        (caseOrganization) => {
          const { organization } = caseOrganization;
          if (organization.fax || organization.beaSafeId) {
            return (
              <MenuItem key={caseOrganization.id} value={"processParticipantsOrganizations." + caseOrganization.id}>
                {translate("participant.labelType.values." + organization.labelType) + " - " + organization.legalName}
              </MenuItem>
            );
          }
        }
      );

      accumulatedRecipientMenuItems = [...accumulatedRecipientMenuItems, ...processParticipantsOrganizationsMenuItems];
    }

    if (product.processParticipantsPeople) {
      const processParticipantsPeopleMenuItems = product.processParticipantsPeople.map((participant: Person) => {
        if (participant.beaSafeId) {
          return (
            <MenuItem key={participant.id} value={"processParticipantsPeople." + participant.id}>
              {translate("participant.labelType.values." + participant.labelType)}
            </MenuItem>
          );
        }
      });

      accumulatedRecipientMenuItems = [...accumulatedRecipientMenuItems, ...processParticipantsPeopleMenuItems];
    }

    accumulatedRecipientMenuItems.push(
      <MenuItem key={"custom"} value={"custom"}>
        Empfänger frei wählen
      </MenuItem>
    );
    return accumulatedRecipientMenuItems;
  }, [product]);
  const communicationMediumEditable = useMemo(() => {
    return (product?.backofficeCase?.lawyer?.person?.beaData && getBeaSafeId(product, recipientType) === "");
  }, [product, recipientType]);

  const { externalReference, externalReferenceIsEditable } = useMemo(
    () => handleReferenceLogic(product, recipientType),
    [product, recipientType]
  );

  const data = {
    communicationMedium,
    setCommunicationMedium,
    recipientType,
    setRecipientType,
    assignedLawyerHasBea,
    communicationMediumEditable,
    recipientMenuItems,
    handleClose: () => {
      setCommunicationMedium("bea");
      setRecipientType("");
      handleClose();
    },
    product,
    externalReference,
    externalReferenceIsEditable,
  };

  return (
    <CommunicationMediumProviderContext.Provider value={data}>{children}</CommunicationMediumProviderContext.Provider>
  );
}

export const useCommunicationMedium = () => {
  const communicationMediumContext = useContext(CommunicationMediumProviderContext);
  if (!communicationMediumContext) {
    throw new Error("useCommunicationMedium can only be used inside CommunicationMediumProvider");
  }
  return communicationMediumContext;
};
